import {Injectable, NgZone} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from "@app/environment";
import {UserService} from "../../../core/user.service";
import {CosoContext, CosoStream, CosoStreamType} from "./datamodel";

const INPUT_AUDIO_MIME_TYPE = 'audio/pcm;rate=16000';
const INPUT_VIDEO_MIME_TYPE = 'image/jpeg';
const OUTPUT_SAMPLE_RATE = 24000;
const PLAYBACK_LEAD_SECONDS = 0.3; // era 0.15: più margine per assorbire i ritardi di Gemini/relay
const PLAYBACK_STOP_FADE_SECONDS = 0.005;
const INPUT_AUDIO_PROCESSOR_NAME = 'coso-live-input-processor';
const INPUT_AUDIO_WORKLET_URL = 'assets/coso-live-input-processor.js';
const VIDEO_MAX_ENCODED_WIDTH = 512;
const VIDEO_FRAME_INTERVAL_MS = 1000;
const VIDEO_JPEG_QUALITY = 0.7;
const MAX_SOCKET_BUFFERED_AMOUNT = 512 * 1024;
const VIDEO_HAVE_CURRENT_DATA = 2;

interface PlaybackSource {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

@Injectable({providedIn: 'root'})
export class CosoLiveService {
  private socket?: WebSocket;
  private mediaStream?: MediaStream;
  private activeMediaStreamSubject = new BehaviorSubject<MediaStream | null>(null);
  readonly activeMediaStream$ = this.activeMediaStreamSubject.asObservable();
  private audioContext?: AudioContext;
  private sourceNode?: MediaStreamAudioSourceNode;
  private processorNode?: AudioWorkletNode;
  private inputSinkNode?: GainNode;
  private videoElement?: HTMLVideoElement;
  private videoCanvas?: HTMLCanvasElement;
  private videoFrameTimer?: number;
  private playbackSources: PlaybackSource[] = [];
  private nextPlaybackTime = 0;

  constructor(private userService: UserService, private ngZone: NgZone) {}

  start(context: CosoContext, userId: number, options: {video?: boolean} = {}): Observable<CosoStream> {
    return new Observable(observer => {
      this.ngZone.runOutsideAngular(() => {
        this.startSession(context, userId, options, observer).catch(error => {
          this.ngZone.run(() => observer.error(error));
        });
      });

      return () => {
        this.stop();
      };
    });
  }

  stop() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({type: 'stop'}));
    }
    this.socket?.close();
    this.socket = undefined;

    this.processorNode?.disconnect();
    this.processorNode?.port.close();
    this.inputSinkNode?.disconnect();
    this.sourceNode?.disconnect();
    this.processorNode = undefined;
    this.inputSinkNode = undefined;
    this.sourceNode = undefined;

    this.stopVideoFrameSampling();
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = undefined;
    this.activeMediaStreamSubject.next(null);

    this.stopPlayback();

    this.audioContext?.close().catch(() => {});
    this.audioContext = undefined;
  }

  async getVideoInputs(): Promise<MediaDeviceInfo[]> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'videoinput');
  }

  // Swaps the camera without restarting the live session
  async switchCamera(deviceId: string) {
    if (!this.mediaStream) {
      return;
    }
    // Stop the current camera first: iOS and some Android devices can't open two at once
    this.mediaStream.getVideoTracks().forEach(track => track.stop());
    const cameraStream = await navigator.mediaDevices.getUserMedia({video: {deviceId: {exact: deviceId}}});
    const stream = new MediaStream([...this.mediaStream.getAudioTracks(), ...cameraStream.getVideoTracks()]);
    this.mediaStream = stream;
    if (this.videoElement) {
      this.videoElement.srcObject = stream;
      void this.videoElement.play().catch(() => {});
    }
    this.activeMediaStreamSubject.next(stream);
  }

  private async startSession(context: CosoContext, userId: number, options: {video?: boolean}, observer: any) {
    this.stop();

    const mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      ...(options.video ? {video: true} : {})
    });
    if (observer.closed) {
      mediaStream.getTracks().forEach(track => track.stop());
      return;
    }
    this.mediaStream = mediaStream;
    this.activeMediaStreamSubject.next(mediaStream);

    const authToken = await this.userService.getJWT();
    if (observer.closed) {
      return;
    }
    if (!authToken) {
      throw new Error('Missing Cognito JWT');
    }

    const audioContext = new AudioContext({sampleRate: OUTPUT_SAMPLE_RATE});
    this.audioContext = audioContext;
    await audioContext.resume();
    if (observer.closed) {
      return;
    }

    const socket = new WebSocket(this.getLiveUrl(authToken));
    this.socket = socket;

    socket.addEventListener('message', event => {
      const message = JSON.parse(event.data);
      if (message.type === CosoStreamType.AI_AUDIO_CHUNK) {
        this.playAudioChunk(message.data);
        return;
      }
      if (message.type === CosoStreamType.AI_AUDIO_INTERRUPTED) {
        console.debug('[CosoLive] AI audio interrupted', {
          timestamp: new Date().toISOString(),
          audioContextTime: this.audioContext?.currentTime,
          queuedPlaybackSources: this.playbackSources.length,
          nextPlaybackTime: this.nextPlaybackTime
        });
        this.stopPlayback();
        return;
      }

      this.ngZone.run(() => observer.next(message as CosoStream));
    });
    socket.addEventListener('error', () => this.ngZone.run(() => observer.error(new Error('Gemini Live connection failed.'))));
    socket.addEventListener('close', () => this.ngZone.run(() => observer.complete()));

    await new Promise<void>((resolve, reject) => {
      socket.onopen = () => resolve();
      socket.addEventListener('error', () => reject(new Error('Gemini Live connection failed.')), {once: true});
    });

    if (observer.closed) {
      return;
    }
    socket.send(JSON.stringify({
      type: 'start',
      context,
      userId
    }));

    if (options.video) {
      this.startVideoFrameSampling(socket);
    }
    this.sourceNode = audioContext.createMediaStreamSource(this.mediaStream);
    const processorNode = await this.createInputProcessorNode(audioContext, socket);
    if (observer.closed) {
      processorNode.disconnect();
      processorNode.port.close();
      return;
    }
    this.processorNode = processorNode;
    this.inputSinkNode = audioContext.createGain();
    this.inputSinkNode.gain.value = 0;

    this.sourceNode.connect(this.processorNode);
    this.processorNode.connect(this.inputSinkNode);
    this.inputSinkNode.connect(audioContext.destination);
  }

  private async createInputProcessorNode(audioContext: AudioContext, socket: WebSocket): Promise<AudioWorkletNode> {
    await audioContext.audioWorklet.addModule(new URL(INPUT_AUDIO_WORKLET_URL, document.baseURI));

    const processorNode = new AudioWorkletNode(audioContext, INPUT_AUDIO_PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1]
    });
    processorNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
      if (socket.readyState !== WebSocket.OPEN) {
        return;
      }

      socket.send(JSON.stringify({
        type: 'audio',
        data: this.floatToBase64Pcm16(event.data, audioContext.sampleRate),
        mimeType: INPUT_AUDIO_MIME_TYPE
      }));
    };

    return processorNode;
  }

  private startVideoFrameSampling(socket: WebSocket) {
    if (!this.mediaStream) {
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.srcObject = this.mediaStream;
    this.videoElement = video;
    this.videoCanvas = document.createElement('canvas');
    void video.play().catch(() => {});

    this.videoFrameTimer = window.setInterval(() => {
      if (socket.readyState !== WebSocket.OPEN || socket.bufferedAmount > MAX_SOCKET_BUFFERED_AMOUNT) {
        return;
      }
      if (!this.videoElement || !this.videoCanvas || this.videoElement.readyState < VIDEO_HAVE_CURRENT_DATA) {
        return;
      }

      const sourceWidth = this.videoElement.videoWidth;
      const sourceHeight = this.videoElement.videoHeight;
      if (!sourceWidth || !sourceHeight) {
        return;
      }

      const width = Math.min(VIDEO_MAX_ENCODED_WIDTH, sourceWidth);
      const height = Math.round(sourceHeight * width / sourceWidth);
      this.videoCanvas.width = width;
      this.videoCanvas.height = height;
      this.videoCanvas.getContext('2d')?.drawImage(this.videoElement, 0, 0, width, height);

      const dataUrl = this.videoCanvas.toDataURL(INPUT_VIDEO_MIME_TYPE, VIDEO_JPEG_QUALITY);
      const data = dataUrl.split(',')[1];
      if (!data) {
        return;
      }

      socket.send(JSON.stringify({
        type: 'video',
        data,
        mimeType: INPUT_VIDEO_MIME_TYPE
      }));
    }, VIDEO_FRAME_INTERVAL_MS);
  }

  private stopVideoFrameSampling() {
    if (this.videoFrameTimer !== undefined) {
      window.clearInterval(this.videoFrameTimer);
      this.videoFrameTimer = undefined;
    }
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
      this.videoElement = undefined;
    }
    this.videoCanvas = undefined;
  }

  private getLiveUrl(authToken: string): string {
    const url = new URL(environment.COSO_API_URL + '/live', window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('apiKey', environment.COSO_API_KEY);
    url.searchParams.set('token', authToken);
    return url.toString();
  }

  private floatToBase64Pcm16(input: Float32Array, inputSampleRate: number): string {
    const ratio = inputSampleRate / 16000;
    const outputLength = Math.max(1, Math.floor(input.length / ratio));
    const output = new Int16Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
      const position = i * ratio;
      const index = Math.floor(position);
      const next = Math.min(input.length - 1, index + 1);
      const fraction = position - index;
      const value = input[index] * (1 - fraction) + input[next] * fraction;
      const sample = Math.max(-1, Math.min(1, value));
      output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }

    return this.bytesToBase64(new Uint8Array(output.buffer));
  }

  private playAudioChunk(base64Pcm16: string) {
    if (!this.audioContext) {
      return;
    }

    const bytes = this.base64ToBytes(base64Pcm16);
    const samples = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / Int16Array.BYTES_PER_ELEMENT));
    const buffer = this.audioContext.createBuffer(1, samples.length, OUTPUT_SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) {
      channel[i] = samples[i] / 0x8000;
    }

    const source = this.audioContext.createBufferSource();
    const gain = this.audioContext.createGain();
    source.buffer = buffer;
    source.connect(gain);
    gain.connect(this.audioContext.destination);
    source.onended = () => {
      source.disconnect();
      gain.disconnect();
      this.playbackSources = this.playbackSources.filter(item => item.source !== source);
    };

    const now = this.audioContext.currentTime;
    const startsNewRun = this.playbackSources.length === 0 || this.nextPlaybackTime <= now;
    if (startsNewRun) {
      const dryMs = (now - this.nextPlaybackTime) * 1000;
      if (dryMs > 0 && dryMs < 1500) {
        console.debug('[CosoLive] playback underrun', {dryMs: Math.round(dryMs)});
      }
      this.nextPlaybackTime = now + PLAYBACK_LEAD_SECONDS;
      gain.gain.setValueAtTime(0, this.nextPlaybackTime);
      gain.gain.linearRampToValueAtTime(1, this.nextPlaybackTime + PLAYBACK_STOP_FADE_SECONDS);
    } else {
      gain.gain.setValueAtTime(1, now);
    }

    const startAt = Math.max(now, this.nextPlaybackTime);
    source.start(startAt);
    this.nextPlaybackTime = startAt + buffer.duration;
    this.playbackSources.push({source, gain});
  }

  private stopPlayback() {
    const stopAt = this.audioContext?.currentTime ?? 0;
    this.playbackSources.forEach(({source, gain}) => {
      try {
        gain.gain.cancelScheduledValues(stopAt);
        gain.gain.setValueAtTime(gain.gain.value, stopAt);
        gain.gain.linearRampToValueAtTime(0, stopAt + PLAYBACK_STOP_FADE_SECONDS);
        source.stop(stopAt + PLAYBACK_STOP_FADE_SECONDS);
      } catch {}
    });
    this.playbackSources = [];
    this.nextPlaybackTime = this.audioContext?.currentTime ?? 0;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
